import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import InputField from '@ingka/input-field';
import Text from '@ingka/text';
import '@ingka/inline-message/dist/style.css';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import * as XLSX from 'xlsx';
import { completeUpload, getSasToken } from '@/api/client';
import AppFooter from '@/components/layout/AppFooter';
import AppHeader from '@/components/layout/AppHeader';
import styles from './LocationsPage.module.css';

interface SingleEntryFormState {
  cmpName: string;
  longitude: string;
  latitude: string;
}

interface FormErrors {
  cmpName?: string;
  longitude?: string;
  latitude?: string;
}

interface BulkRow {
  rowNumber: number; // 1-indexed original row from the XLSX
  cmpName: string;
  longitude: string;
  latitude: string;
  errors: string[]; // validation errors for this row
}

interface BulkParseResult {
  validRows: BulkRow[];
  invalidRows: BulkRow[];
}

function validateSingleEntry(form: SingleEntryFormState): FormErrors {
  const errors: FormErrors = {};

  if (!form.cmpName.trim()) {
    errors.cmpName = 'CMP name is required';
  }

  const lon = Number(form.longitude);
  if (!form.longitude.trim()) {
    errors.longitude = 'Longitude is required';
  } else if (Number.isNaN(lon)) {
    errors.longitude = 'Longitude must be a number';
  } else if (lon < -180 || lon > 180) {
    errors.longitude = 'Longitude must be between -180 and 180';
  }

  const lat = Number(form.latitude);
  if (!form.latitude.trim()) {
    errors.latitude = 'Latitude is required';
  } else if (Number.isNaN(lat)) {
    errors.latitude = 'Latitude must be a number';
  } else if (lat < -90 || lat > 90) {
    errors.latitude = 'Latitude must be between -90 and 90';
  }

  return errors;
}

function buildTimestampedFilename(): string {
  const now = new Date();
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(now.getUTCDate()).padStart(2, '0');
  const HH = String(now.getUTCHours()).padStart(2, '0');
  const MM = String(now.getUTCMinutes()).padStart(2, '0');
  const SS = String(now.getUTCSeconds()).padStart(2, '0');
  return `web_locations_cmpgeoloc_${yyyy}${mm}${dd}_${HH}${MM}${SS}_internal.csv`;
}

function buildCsvBlob(rows: BulkRow[]): Blob {
  // Escape a CSV field: quote if it contains commas, quotes, or newlines
  const escapeCsvField = (val: string): string => {
    if (val.includes(',') || val.includes('"') || val.includes('\n')) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };

  const header = 'cmp_name,longitude,latitude';
  const dataLines = rows.map((row) =>
    [escapeCsvField(row.cmpName), escapeCsvField(row.longitude), escapeCsvField(row.latitude)].join(
      ',',
    ),
  );
  const csv = `${[header, ...dataLines].join('\n')}\n`;
  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
}

function validateBulkRow(row: BulkRow): string[] {
  const errors: string[] = [];

  if (!row.cmpName.trim()) {
    errors.push('CMP name is empty');
  }

  const lon = Number(row.longitude);
  if (row.longitude === '' || row.longitude === null) {
    errors.push('Longitude is empty');
  } else if (Number.isNaN(lon)) {
    errors.push('Longitude is not a number');
  } else if (lon < -180 || lon > 180) {
    errors.push('Longitude out of range (-180 to 180)');
  }

  const lat = Number(row.latitude);
  if (row.latitude === '' || row.latitude === null) {
    errors.push('Latitude is empty');
  } else if (Number.isNaN(lat)) {
    errors.push('Latitude is not a number');
  } else if (lat < -90 || lat > 90) {
    errors.push('Latitude out of range (-90 to 90)');
  }

  return errors;
}

async function parseBulkFile(file: File): Promise<BulkParseResult> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('The file has no sheets.');
  }
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    throw new Error('The file has no sheets.');
  }

  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: '',
    raw: false,
  });

  if (rows.length === 0) {
    throw new Error('The file has no data rows.');
  }

  const validRows: BulkRow[] = [];
  const invalidRows: BulkRow[] = [];

  rows.forEach((rawRow, idx) => {
    const normalized: Record<string, string> = {};
    for (const key of Object.keys(rawRow)) {
      normalized[key.toLowerCase().trim()] = String(rawRow[key] ?? '').trim();
    }

    const row: BulkRow = {
      rowNumber: idx + 1,
      cmpName: normalized.cmp_name ?? '',
      longitude: normalized.longitude ?? '',
      latitude: normalized.latitude ?? '',
      errors: [],
    };

    row.errors = validateBulkRow(row);

    if (row.errors.length === 0) {
      validRows.push(row);
    } else {
      invalidRows.push(row);
    }
  });

  return { validRows, invalidRows };
}

export default function LocationsPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<SingleEntryFormState>({
    cmpName: '',
    longitude: '',
    latitude: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkParseResult | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [bulkSubmitError, setBulkSubmitError] = useState<string | null>(null);

  const handleSingleSubmit = async () => {
    const validationErrors = validateSingleEntry(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const row: BulkRow = {
        rowNumber: 1,
        cmpName: form.cmpName.trim(),
        longitude: form.longitude.trim(),
        latitude: form.latitude.trim(),
        errors: [],
      };
      const blob = buildCsvBlob([row]);
      const filename = buildTimestampedFilename();

      // Get SAS token
      const displayName = form.cmpName.trim();
      const { sasUrl, uploadId } = await getSasToken(
        'locations',
        'cmpgeoloc',
        'internal',
        filename,
        displayName,
      );

      // Upload directly to blob storage using @azure/storage-blob
      const { BlockBlobClient } = await import('@azure/storage-blob');
      const blockBlobClient = new BlockBlobClient(sasUrl);
      await blockBlobClient.uploadData(blob, {
        blobHTTPHeaders: { blobContentType: 'text/csv' },
      });

      // Complete the upload server-side
      await completeUpload(uploadId, blob.size, undefined, 1);

      // Navigate to success page (Stage 8)
      navigate('/locations/success?count=1');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: keyof SingleEntryFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error on that field as user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBulkFile = async (file: File) => {
    setBulkError(null);
    setBulkResult(null);
    setBulkFile(null);

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'xlsx' && ext !== 'xls') {
      setBulkError('Please upload an XLSX or XLS file.');
      return;
    }

    try {
      const result = await parseBulkFile(file);
      setBulkFile(file);
      setBulkResult(result);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Failed to read the file.');
    }
  };

  const onBulkDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onBulkDragLeave = () => setDragActive(false);

  const onBulkDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleBulkFile(dropped);
  };

  const cancelBulk = () => {
    if (isBulkSubmitting) return;
    setBulkFile(null);
    setBulkResult(null);
    setBulkError(null);
    setBulkSubmitError(null);
  };

  const handleBulkSubmit = async () => {
    if (!bulkResult || bulkResult.invalidRows.length > 0 || bulkResult.validRows.length === 0) {
      return;
    }

    setIsBulkSubmitting(true);
    setBulkSubmitError(null);

    try {
      const csvBlob = buildCsvBlob(bulkResult.validRows);
      const filename = buildTimestampedFilename();

      const { sasUrl, uploadId } = await getSasToken(
        'locations',
        'cmpgeoloc',
        'internal',
        filename,
        bulkFile?.name,
      );

      const { BlockBlobClient } = await import('@azure/storage-blob');
      const blockBlobClient = new BlockBlobClient(sasUrl);
      await blockBlobClient.uploadData(csvBlob, {
        blobHTTPHeaders: { blobContentType: 'text/csv' },
      });

      await completeUpload(uploadId, csvBlob.size, undefined, bulkResult.validRows.length);

      navigate(`/locations/success?count=${bulkResult.validRows.length}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setBulkSubmitError(message);
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  return (
    <>
      <AppHeader />
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.header}>
            <Text tagName="h1" textStyle="Heading.L">
              Add Locations for the Customer Meeting Points
            </Text>
            <Text tagName="p" textStyle="Body.L" className={styles.subtitle}>
              Add a single CMP with its coordinates, or upload many at once.
            </Text>
          </div>

          <div className={styles.panels}>
            {/* Left panel — single entry */}
            <section className={styles.panel} aria-labelledby="single-entry-title">
              <Text
                tagName="h2"
                textStyle="Heading.M"
                id="single-entry-title"
                className={styles.panelTitle}
              >
                Add a single CMP
              </Text>
              <Text tagName="p" textStyle="Body.M" className={styles.panelSubtitle}>
                Enter the CMP name and its coordinates.
              </Text>

              <div className={styles.panelBody}>
                <div className={styles.formFields}>
                  <InputField
                    id="cmp-name"
                    label="CMP name"
                    type="text"
                    value={form.cmpName}
                    onChange={(e) => updateField('cmpName', e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.cmpName && <div className={styles.fieldError}>{errors.cmpName}</div>}
                  <InputField
                    id="longitude"
                    label="Longitude"
                    type="text"
                    value={form.longitude}
                    onChange={(e) => updateField('longitude', e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.longitude && <div className={styles.fieldError}>{errors.longitude}</div>}
                  <InputField
                    id="latitude"
                    label="Latitude"
                    type="text"
                    value={form.latitude}
                    onChange={(e) => updateField('latitude', e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.latitude && <div className={styles.fieldError}>{errors.latitude}</div>}
                </div>

                {submitError && (
                  <div className={styles.submitError}>
                    <InlineMessage variant="negative" title="Submit failed" body={submitError} />
                  </div>
                )}

                <div className={styles.formActions}>
                  <Button
                    type="emphasised"
                    text={isSubmitting ? 'Submitting…' : 'Submit'}
                    disabled={isSubmitting}
                    onClick={handleSingleSubmit}
                  />
                </div>
              </div>
            </section>

            {/* Right panel — bulk upload */}
            <section className={styles.panel} aria-labelledby="bulk-upload-title">
              <Text
                tagName="h2"
                textStyle="Heading.M"
                id="bulk-upload-title"
                className={styles.panelTitle}
              >
                Bulk upload
              </Text>
              <Text tagName="p" textStyle="Body.M" className={styles.panelSubtitle}>
                Download the template, fill it in, and upload it back.
              </Text>
              <div className={styles.panelBody}>
                {/* Step 1: Download template */}
                <div className={styles.templateBlock}>
                  <Text tagName="p" textStyle="Body.M">
                    Step 1: Download the XLSX template with the required columns.
                  </Text>
                  <a href="/locations-template.xlsx" download className={styles.templateLink}>
                    <Button type="secondary" text="Download template" />
                  </a>
                </div>

                {/* Step 2: Upload dropzone */}
                {!bulkFile && (
                  <div className={styles.bulkStep}>
                    <Text tagName="p" textStyle="Body.M">
                      Step 2: Upload the filled-in file.
                    </Text>
                    <input
                      ref={bulkInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleBulkFile(f);
                      }}
                      style={{ display: 'none' }}
                      aria-hidden
                    />
                    <button
                      type="button"
                      onDragOver={onBulkDragOver}
                      onDragLeave={onBulkDragLeave}
                      onDrop={onBulkDrop}
                      onClick={() => bulkInputRef.current?.click()}
                      className={`${styles.dropzone}${dragActive ? ` ${styles.dropzoneActive}` : ''}`}
                      aria-label="Bulk upload drop zone"
                    >
                      <Text tagName="p" textStyle="Body.L">
                        {dragActive ? 'Drop your file here' : 'Drag an XLSX file here'}
                      </Text>
                      <Text tagName="p" textStyle="Body.S" className={styles.dropzoneHint}>
                        or click to browse
                      </Text>
                    </button>

                    {bulkError && (
                      <div className={styles.bulkError}>
                        <InlineMessage
                          variant="negative"
                          title="Couldn't read the file"
                          body={bulkError}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Preview */}
                {bulkResult && bulkFile && (
                  <div className={styles.bulkPreview}>
                    <div className={styles.previewHeader}>
                      <div>
                        <Text tagName="p" textStyle="Body.M">
                          <strong>{bulkFile.name}</strong>
                        </Text>
                        <Text tagName="p" textStyle="Body.S" className={styles.previewCount}>
                          {bulkResult.validRows.length} valid, {bulkResult.invalidRows.length}{' '}
                          invalid
                        </Text>
                      </div>
                      <Button type="secondary" text="Choose different file" onClick={cancelBulk} />
                    </div>

                    {bulkResult.invalidRows.length > 0 && (
                      <div className={styles.invalidBlock}>
                        <InlineMessage
                          variant="negative"
                          title={`${bulkResult.invalidRows.length} invalid row${bulkResult.invalidRows.length === 1 ? '' : 's'}`}
                          body="Fix these rows in your file and upload again. You can't submit until all rows are valid."
                        />
                        <table className={styles.invalidTable}>
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>CMP name</th>
                              <th>Longitude</th>
                              <th>Latitude</th>
                              <th>Issues</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkResult.invalidRows.map((row) => (
                              <tr key={row.rowNumber}>
                                <td>{row.rowNumber}</td>
                                <td>{row.cmpName || <em>empty</em>}</td>
                                <td>{row.longitude || <em>empty</em>}</td>
                                <td>{row.latitude || <em>empty</em>}</td>
                                <td>{row.errors.join('; ')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {bulkResult.invalidRows.length === 0 && bulkResult.validRows.length > 0 && (
                      <div className={styles.validBlock}>
                        <InlineMessage
                          variant="positive"
                          title={`Ready to upload ${bulkResult.validRows.length} row${bulkResult.validRows.length === 1 ? '' : 's'}`}
                          body="Review the preview below and click Upload to submit."
                        />
                        <div className={styles.previewTableWrap}>
                          <table className={styles.previewTable}>
                            <thead>
                              <tr>
                                <th>Row</th>
                                <th>CMP name</th>
                                <th>Longitude</th>
                                <th>Latitude</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bulkResult.validRows.slice(0, 10).map((row) => (
                                <tr key={row.rowNumber}>
                                  <td>{row.rowNumber}</td>
                                  <td>{row.cmpName}</td>
                                  <td>{row.longitude}</td>
                                  <td>{row.latitude}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {bulkResult.validRows.length > 10 && (
                            <Text tagName="p" textStyle="Body.S" className={styles.previewMore}>
                              Showing first 10 of {bulkResult.validRows.length} rows.
                            </Text>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={styles.bulkActions}>
                      <Button
                        type="emphasised"
                        text={isBulkSubmitting ? 'Uploading…' : 'Upload'}
                        disabled={
                          isBulkSubmitting ||
                          bulkResult.invalidRows.length > 0 ||
                          bulkResult.validRows.length === 0
                        }
                        onClick={handleBulkSubmit}
                      />
                      <Button
                        type="tertiary"
                        text="Cancel"
                        disabled={isBulkSubmitting}
                        onClick={cancelBulk}
                      />
                    </div>

                    {bulkSubmitError && (
                      <div className={styles.bulkError}>
                        <InlineMessage
                          variant="negative"
                          title="Upload failed"
                          body={bulkSubmitError}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
      <AppFooter />
    </>
  );
}
